import { sanitizeSearchInput } from './strutil';

// 검색 키워드 전처리 공용 helper.
// /api/search 계열 호출부가 sanitize 를 빼먹지 않도록 + VERBATIM 케이스의
// 첫 공백 → %2b 변환 (기존 호환 동작) 을 한 곳에 모은다.
export const prepareSearchKeyword = (keyword: string, resultType?: string): string => {
  const sanitized = sanitizeSearchInput(keyword);
  return resultType === 'VERBATIM' ? sanitized.replace(' ', '%2b') : sanitized;
};

type BuildNewSearchUrlArgs = {
  country: string;
  lang: string;
  input: string;
  speller?: string;
};

// /newSearch 로 이동할 때 쓰는 URL 빌더.
// 입력을 sanitize 해서 Azure WAF CRS 3.2 921151 (GET arg value 의 CR/LF) 을 비롯한
// 비정상 문자 기반 차단을 사전에 회피한다.
export const buildNewSearchUrl = ({ country, lang, input, speller }: BuildNewSearchUrlArgs): string => {
  const cleanInput = sanitizeSearchInput(input);
  const base = `/${country}/${lang}/newSearch?searchInput=`;
  if (speller) {
    const cleanSpeller = sanitizeSearchInput(speller);
    return `${base}${encodeURIComponent(cleanSpeller)}&alt=${encodeURIComponent(cleanInput)}`;
  }
  return `${base}${encodeURIComponent(cleanInput)}`;
};

export const buildSearch3Url = ({ country, lang, input, speller }: BuildNewSearchUrlArgs): string => {
  const cleanInput = sanitizeSearchInput(input);
  const base = `/${country}/${lang}/search3?searchInput=`;
  if (speller) {
    const cleanSpeller = sanitizeSearchInput(speller);
    return `${base}${encodeURIComponent(cleanSpeller)}&alt=${encodeURIComponent(cleanInput)}`;
  }
  return `${base}${encodeURIComponent(cleanInput)}`;
};

// /api/speller?query=... 를 호출할 때 쓰는 URL 빌더.
// /newSearch 이동 전에 speller 가 먼저 WAF 921151 에 막히는 것을 방지한다.
export const buildSpellerQueryUrl = (input: string): string => {
  const cleanInput = sanitizeSearchInput(input);
  return `/api/speller?query=${encodeURIComponent(cleanInput)}`;
};

// /api/suggest?query=... 용 URL 빌더.
// 기존 2중 encodeURIComponent 구조를 유지하면서 raw 입력의 CR/LF 를 제거한다.
export const buildSuggestQueryUrl = (input: string, reqlang: string): string => {
  const cleanInput = sanitizeSearchInput(input);
  const inner = `query=${encodeURIComponent(cleanInput)}&service=index_${reqlang}`;
  return `/api/suggest?query=${encodeURIComponent(inner)}`;
};

/*
응답형식은 아래와 같다. 
{
  "result": [
    "프로서치",
    "검색엔진",
    "프로텐"
  ],
  "isOk": true,
  "count": 3
}
*/
export const buildSuggestQueryV3Url = (input: string): string => {
  const cleanInput = sanitizeSearchInput(input);
  return `/api/guest/srch/v3/suggestQuery?query=${encodeURIComponent(cleanInput)}`;
};

// /api/related?query=... 용 URL 빌더.
// suggest 와 동일한 2중 encodeURIComponent 구조.
export const buildRelatedQueryUrl = (input: string, service: string): string => {
  const cleanInput = sanitizeSearchInput(input);
  const inner = `query=${encodeURIComponent(cleanInput)}&service=${service}`;
  return `/api/related?query=${encodeURIComponent(inner)}`;
};

// /api/search?query=... 용 outer URL 빌더.
// inner query 조립은 호출 지점이 담당 (filter/doctype/tags 등 복잡한 조건 때문).
// 호출부에서 searchKeyword 는 sanitizeSearchInput 을 거친 값을 쓸 것.
// outerSuffix 는 encode 되지 않고 그대로 이어붙음 (예: `&tag=...` 추가 파라미터).
export const buildSearchApiUrl = (innerQuery: string, outerSuffix = ''): string => {
  return `/api/search?query=${encodeURIComponent(innerQuery)}${outerSuffix}`;
};
